// =============================================================================
// gruService — UNICO file che conosce tabelle, view e funzioni SQL del modulo GRU.
// La UI usa soltanto le chiavi "entita" (es. 'gru', 'reparti') e mai i nomi SQL.
// Tutte le liste passano da .range() con count: nessun select senza limite.
// =============================================================================
import { supabase } from '../supabase';

// ─── Errori ──────────────────────────────────────────────────────────────────

export class GruError extends Error {
  constructor({ code, message, campo = null, detail = null, originale = null }) {
    super(message);
    this.name = 'GruError';
    this.code = code;
    this.campo = campo;
    this.detail = detail;
    this.originale = originale;
  }
}

// Codici applicativi (message dell'eccezione SQL) -> messaggio italiano
const MESSAGGI_CODICE = {
  NON_AUTORIZZATO: 'Non hai i permessi per eseguire questa operazione.',
  IDEMPOTENCY_KEY_OBBLIGATORIA: 'Richiesta non valida: chiave di idempotenza mancante.',
  IDEMPOTENCY_CONFLITTO: 'Questa richiesta è già stata usata per un\'altra operazione.',
  QTA_NON_VALIDA: 'Quantità non valida per questa unità di misura.',
  QTA_INSUFFICIENTE: 'Quantità insufficiente in magazzino.',
  QTA_PROTETTA: 'La giacenza si modifica solo registrando un movimento.',
  ARTICOLO_NON_TROVATO: 'Articolo non trovato.',
  ARTICOLO_DISATTIVO: 'L\'articolo è disattivato.',
  TIPO_NON_VALIDO: 'Tipo di intervento non valido o disattivato.',
  GRU_NON_TROVATA: 'Gru non trovata o disattivata.',
  COMPONENTE_NON_VALIDO: 'Il componente non appartiene a questa gru o è disattivato.',
  CAUSA_NON_VALIDA: 'Causa non valida o disattivata.',
  DATA_NON_VALIDA: 'Data non valida.',
  EAN_NON_VALIDO: 'Codice EAN-13 non valido (13 cifre con cifra di controllo corretta).',
  EAN_DUPLICATO: 'Esiste già un articolo con questo codice EAN.',
  NOME_OBBLIGATORIO: 'Il nome è obbligatorio.',
  RIFERIMENTO_NON_VALIDO: 'Uno dei valori selezionati non esiste più.',
  ATTRIBUTI_NON_VALIDI: 'Uno o più campi tecnici non sono validi.',
  MOTIVO_OBBLIGATORIO: 'Il motivo è obbligatorio.',
  MOVIMENTO_NON_TROVATO: 'Movimento non trovato.',
  MOVIMENTO_NON_STORNABILE: 'Questo movimento non può essere stornato.',
  MOVIMENTO_GIA_STORNATO: 'Questo movimento è già stato stornato.',
  MOVIMENTO_IMMUTABILE: 'I movimenti non si modificano né si cancellano: usa lo storno.',
  INTERVENTO_IMMUTABILE: 'Gli interventi non si modificano né si cancellano.',
  INTERVENTO_NON_TROVATO: 'Intervento non trovato.',
  ORDINE_NON_VALIDO: 'L\'ordine selezionato non è valido per questo articolo.',
  ORDINE_CHIUSO: 'L\'ordine è già chiuso.',
  RECORD_IN_USO: 'Impossibile eliminare: l\'elemento è collegato ad altri dati. Disattivalo invece di eliminarlo.',
  RECORD_NON_TROVATO: 'Elemento non trovato (forse è già stato eliminato).',
  TABELLA_NON_VALIDA: 'Operazione non consentita su questa tabella.',
  KEY_IMMUTABILE: 'La chiave non è modificabile dopo la creazione.',
  CAMPO_IN_USO: 'Il campo è già usato da alcuni dati: non si può cambiare tipo o rimuovere opzioni in uso.',
  TIPO_IN_USO: 'Il comportamento non è modificabile: esistono già interventi di questo tipo.',
  UNITA_BLOCCATA: 'L\'unità di misura non si cambia se esistono già movimenti.',
  NESSUN_RECORD: 'Nessun elemento aggiornato: controlla i permessi o riprova.',
  INTERVENTO_ANNULLATO: 'Questo intervento è già stato annullato.',
  ORDINE_CON_RICEZIONI: 'L\'ordine ha già delle ricezioni: non si può cambiare articolo o tipo.',
  RICEZIONE_IMMUTABILE: 'Le ricezioni degli ordini non si modificano né si cancellano.',
  SESSIONE_SCADUTA: 'Sessione scaduta: effettua di nuovo il login.',
  RETE: 'Connessione non disponibile. Riprova tra poco.',
};

// Vincoli Postgres (nome) -> campo del form + messaggio
const VINCOLI = {
  gru_reparti_key_uq:           { campo: 'key',    messaggio: 'Esiste già un reparto con questa chiave.' },
  gru_campi_key_uq:             { campo: 'key',    messaggio: 'Esiste già un campo con questa chiave in questo reparto.' },
  gru_cause_key_uq:             { campo: 'key',    messaggio: 'Esiste già una causa con questa chiave.' },
  gru_tipi_intervento_key_uq:   { campo: 'key',    messaggio: 'Esiste già un tipo di intervento con questa chiave.' },
  gru_magazzini_key_uq:         { campo: 'key',    messaggio: 'Esiste già un magazzino con questa chiave.' },
  gru_unita_misura_key_uq:      { campo: 'key',    messaggio: 'Esiste già un\'unità di misura con questa chiave.' },
  gru_ubicazioni_codice_uq:     { campo: 'codice', messaggio: 'Esiste già un\'ubicazione con questo codice in questo magazzino.' },
  gru_gru_codice_uq:            { campo: 'codice', messaggio: 'Esiste già una gru con questo codice.' },
  gru_articoli_ean_uq:          { campo: 'ean',    messaggio: 'Esiste già un articolo con questo codice EAN.' },
  gru_compatibilita_coppia_uq:  { campo: null,     messaggio: 'Questo articolo è già associato a questo componente.' },
  gru_compatibilita_primario_uq:{ campo: null,     messaggio: 'Questo componente ha già un articolo primario.' },
  gru_reparti_key_ck:           { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_campi_key_ck:             { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_cause_key_ck:             { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_tipi_intervento_key_ck:   { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_magazzini_key_ck:         { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_unita_misura_key_ck:      { campo: 'key',    messaggio: 'La chiave può contenere solo lettere minuscole, numeri e _.' },
  gru_reparti_label_ck:         { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_campi_label_ck:           { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_cause_label_ck:           { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_tipi_intervento_label_ck: { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_magazzini_label_ck:       { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_unita_misura_label_ck:    { campo: 'label',  messaggio: 'Obbligatorio' },
  gru_ubicazioni_codice_ck:     { campo: 'codice', messaggio: 'Obbligatorio' },
  gru_gru_codice_ck:            { campo: 'codice', messaggio: 'Obbligatorio' },
  gru_gru_nome_ck:              { campo: 'nome',   messaggio: 'Obbligatorio' },
  gru_campi_opzioni_ck:         { campo: 'opzioni', messaggio: 'Le opzioni non sono valide: servono chiave ed etichetta, senza duplicati.' },
  gru_campi_tipo_ck:            { campo: 'tipo',   messaggio: 'Tipo di campo non valido.' },
  gru_unita_misura_decimali_ck: { campo: 'decimali', messaggio: 'I decimali devono essere tra 0 e 3.' },
  gru_componenti_posizione_ck:  { campo: 'posizione', messaggio: 'La posizione deve essere SX, DX o vuota.' },
  gru_tipi_intervento_mov_ck:   { campo: 'tipo_movimento', messaggio: 'Comportamento non valido.' },
};

const estraiVincolo = (testo) => {
  const m = /constraint "([^"]+)"/.exec(testo || '');
  return m ? m[1] : null;
};

// "campo obbligatorio: diametro" -> 'attributi.diametro' (per segnalare l'errore sul campo giusto)
const estraiCampoAttributo = (detail) => {
  const m = /(?:campo obbligatorio|valore non valido per|campo sconosciuto|campo disattivato):?\s*([a-z0-9_]+)/.exec(detail || '');
  return m ? `attributi.${m[1]}` : null;
};

/**
 * Converte QUALSIASI errore (codice applicativo, errore Postgres 23505/23503/..., rete)
 * in un GruError con messaggio italiano. Unico punto di mappatura.
 */
export const mapGruError = (err) => {
  if (err instanceof GruError) return err;

  const msg = String(err?.message ?? '');
  const detail = err?.details ?? err?.detail ?? null;
  const sqlstate = err?.code;

  // 1) codice applicativo sollevato dalle funzioni/trigger: message == CODICE
  if (/^[A-Z][A-Z0-9_]{2,}$/.test(msg)) {
    const base = MESSAGGI_CODICE[msg] ?? `Errore: ${msg}`;
    const campo = msg === 'ATTRIBUTI_NON_VALIDI' ? estraiCampoAttributo(detail) : null;
    const dettaglio = msg === 'ATTRIBUTI_NON_VALIDI' && detail ? `${base} (${detail})` : base;
    return new GruError({ code: msg, message: dettaglio, campo, detail, originale: err });
  }

  // 2) errori Postgres
  if (sqlstate === '23505') {
    const v = VINCOLI[estraiVincolo(msg)];
    if (v) return new GruError({ code: 'DUPLICATO', message: v.messaggio, campo: v.campo, detail, originale: err });
    return new GruError({ code: 'DUPLICATO', message: 'Esiste già un elemento con questi valori.', detail, originale: err });
  }

  if (sqlstate === '23503') {
    // cancellazione/modifica di una riga referenziata altrove
    if (/update or delete on table/i.test(msg)) {
      return new GruError({ code: 'RECORD_IN_USO', message: MESSAGGI_CODICE.RECORD_IN_USO, detail, originale: err });
    }
    return new GruError({ code: 'RIFERIMENTO_NON_VALIDO', message: MESSAGGI_CODICE.RIFERIMENTO_NON_VALIDO, detail, originale: err });
  }

  if (sqlstate === '23514') {
    const v = VINCOLI[estraiVincolo(msg)];
    return new GruError({
      code: 'VALORE_NON_VALIDO',
      message: v ? v.messaggio : 'Valore non valido.',
      campo: v ? v.campo : null,
      detail,
      originale: err,
    });
  }

  if (sqlstate === '23502') {
    const m = /column "([^"]+)"/.exec(msg);
    return new GruError({
      code: 'CAMPO_OBBLIGATORIO',
      message: 'Campo obbligatorio.',
      campo: m ? m[1] : null,
      detail,
      originale: err,
    });
  }

  if (sqlstate === '42501' || /row-level security|permission denied/i.test(msg)) {
    return new GruError({ code: 'NON_AUTORIZZATO', message: MESSAGGI_CODICE.NON_AUTORIZZATO, detail, originale: err });
  }

  if (sqlstate === 'PGRST301' || /jwt/i.test(msg)) {
    return new GruError({ code: 'SESSIONE_SCADUTA', message: MESSAGGI_CODICE.SESSIONE_SCADUTA, detail, originale: err });
  }

  if (/failed to fetch|networkerror|load failed/i.test(msg)) {
    return new GruError({ code: 'RETE', message: MESSAGGI_CODICE.RETE, detail, originale: err });
  }

  return new GruError({
    code: 'ERRORE',
    message: `Errore imprevisto${msg ? `: ${msg}` : '.'}`,
    detail,
    originale: err,
  });
};

// ─── Entità ──────────────────────────────────────────────────────────────────
// La UI conosce solo le chiavi di questa mappa.

const ENTITA = {
  gru:        { tabella: 'gru_gru',             ricerca: ['codice', 'nome', 'descrizione'], ordine: { key: 'codice', direction: 'asc' } },
  componenti: { tabella: 'gru_componenti',      vista: 'gru_v_componenti',
                ricerca: ['gru_codice', 'gru_nome', 'reparto_label', 'etichetta'],         ordine: { key: 'gru_codice', direction: 'asc' } },
  reparti:    { tabella: 'gru_reparti',         ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  campi:      { tabella: 'gru_campi',           ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  cause:      { tabella: 'gru_cause',           ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  tipi:       { tabella: 'gru_tipi_intervento', ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  unita:      { tabella: 'gru_unita_misura',    ricerca: ['key', 'label'],                  ordine: { key: 'key', direction: 'asc' } },
  magazzini:  { tabella: 'gru_magazzini',       ricerca: ['key', 'label'],                  ordine: { key: 'ordine', direction: 'asc' } },
  ubicazioni: { tabella: 'gru_ubicazioni',      ricerca: ['codice', 'descrizione'],         ordine: { key: 'codice', direction: 'asc' } },
  articoli:   { tabella: 'gru_articoli',        vista: 'gru_v_articoli',
                ricerca: ['ean', 'nome', 'codice_sap', 'codice_fornitore'],               ordine: { key: 'nome', direction: 'asc' } },
  compatibilita: { tabella: 'gru_compatibilita', vista: 'gru_v_compatibilita',
                ricerca: ['gru_codice', 'reparto_label', 'componente_etichetta'],         ordine: { key: 'gru_codice', direction: 'asc' } },
  // view senza colonna "attivo" (registro): niente filtro attivi
  storico:    { vista: 'gru_v_storico', senzaAttivo: true,
                ricerca: ['ean', 'nome', 'utente_nome'],                                  ordine: { key: 'id', direction: 'desc' } },
  interventi: { vista: 'gru_v_interventi', senzaAttivo: true,
                ricerca: ['ean', 'nome', 'gru_codice'],                                   ordine: { key: 'data_intervento', direction: 'desc' } },
  audit:      { vista: 'gru_v_audit_qta', senzaAttivo: true, chiaveUnica: 'articolo_id',
                ricerca: ['ean', 'nome'],                                                 ordine: { key: 'nome', direction: 'asc' } },
};

const config = (entita) => {
  const c = ENTITA[entita];
  if (!c) throw new Error(`Entità GRU sconosciuta: ${entita}`);
  return c;
};

// Rimuove i caratteri che romperebbero il filtro .or() di PostgREST
const pulisciRicerca = (testo) => String(testo ?? '').replace(/[%,()*\\]/g, ' ').trim();

// Filtri: valore semplice = uguaglianza; oggetti con operatore:
//   { contiene: v }  la colonna array contiene v      (es. gru_ids)
//   { ilike: testo } la colonna testuale contiene il testo, senza distinguere maiuscole
//   { diverso: v }   la colonna è diversa da v
//   { da, a }        intervallo di date (stringhe YYYY-MM-DD), uno dei due estremi può mancare
const applicaFiltri = (query, filtri) => {
  let q = query;
  Object.entries(filtri || {}).forEach(([chiave, valore]) => {
    if (valore === null || valore === undefined || valore === '') return;
    if (typeof valore === 'object') {
      if (valore.da !== undefined || valore.a !== undefined) {
        // intervallo di date (YYYY-MM-DD), estremi inclusi
        if (valore.da) q = q.gte(chiave, `${valore.da}T00:00:00`);
        if (valore.a) q = q.lte(chiave, `${valore.a}T23:59:59.999`);
        return;
      }
      if (valore.diverso !== undefined) {
        q = q.neq(chiave, valore.diverso);
        return;
      }
      if (valore.contiene !== undefined && valore.contiene !== '') q = q.contains(chiave, [valore.contiene]);
      else if (valore.ilike !== undefined) {
        const t = pulisciRicerca(valore.ilike);
        if (t) q = q.ilike(chiave, `%${t}%`);
      }
      return;
    }
    q = q.eq(chiave, valore);
  });
  return q;
};

// ─── Letture ─────────────────────────────────────────────────────────────────

/**
 * Lista paginata lato server.
 * opzioni: { pagina (0-based), perPagina, sort {key,direction}, ricerca, soloAttivi, filtri {col: valore} }
 * Ritorna { righe, totale }.
 */
export const gruLista = async (entita, opzioni = {}) => {
  const {
    pagina = 0,
    perPagina = 25,
    sort = null,
    ricerca = '',
    soloAttivi = true,
    filtri = {},
  } = opzioni;
  const c = config(entita);
  const da = pagina * perPagina;
  const a = da + perPagina - 1;

  let q = supabase.from(c.vista || c.tabella).select('*', { count: 'exact' });
  if (soloAttivi && !c.senzaAttivo) q = q.eq('attivo', true);
  q = applicaFiltri(q, filtri);

  const termine = pulisciRicerca(ricerca);
  if (termine && c.ricerca?.length) {
    q = q.or(c.ricerca.map((col) => `${col}.ilike.%${termine}%`).join(','));
  }

  const ord = sort?.key ? sort : c.ordine;
  const chiaveUnica = c.chiaveUnica || 'id';
  q = q.order(ord.key, { ascending: ord.direction !== 'desc' });
  if (ord.key !== chiaveUnica) q = q.order(chiaveUnica, { ascending: true });

  const { data, error, count } = await q.range(da, a);
  if (error) throw mapGruError(error);
  return { righe: data ?? [], totale: count ?? 0 };
};

/**
 * Tutte le righe che corrispondono a ricerca/filtri/ordinamento (per l'esportazione),
 * lette a blocchi con .range(). Si ferma a `massimo` righe.
 * Ritorna { righe, totale, troncato }.
 */
export const gruEsporta = async (entita, opzioni = {}, massimo = 10000) => {
  const perPagina = 500;
  let pagina = 0;
  let righe = [];
  let totale = 0;
  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const res = await gruLista(entita, { ...opzioni, pagina, perPagina });
    totale = res.totale;
    righe = righe.concat(res.righe);
    if (res.righe.length === 0 || righe.length >= totale || righe.length >= massimo) break;
    pagina += 1;
  }
  return { righe: righe.slice(0, massimo), totale, troncato: totale > massimo };
};

/**
 * Elenco per i dropdown (attivi e disattivati: la UI decide cosa mostrare).
 * Limitato a 1000 righe (.range) — i sifarnici GRU sono molto più piccoli.
 */
export const gruOpzioni = async (entita, filtri = {}) => {
  const c = config(entita);
  let q = supabase.from(c.vista || c.tabella).select('*');
  q = applicaFiltri(q, filtri);
  q = q.order(c.ordine.key, { ascending: c.ordine.direction !== 'desc' }).order('id', { ascending: true });
  const { data, error } = await q.range(0, 999);
  if (error) throw mapGruError(error);
  return data ?? [];
};

/** Campi (gru_campi) di un reparto, ordinati. Include i disattivati. */
export const gruCampiReparto = (repartoId) => gruOpzioni('campi', { reparto_id: repartoId });

/** Singolo articolo dalla view (con reparto, unità, ubicazione, stato scorta). null se non esiste. */
export const gruArticolo = async (id) => {
  const { righe } = await gruLista('articoli', { perPagina: 1, soloAttivi: false, filtri: { id } });
  return righe[0] ?? null;
};

// ─── Scritture ───────────────────────────────────────────────────────────────

export const gruCrea = async (entita, payload) => {
  const c = config(entita);
  const { data, error } = await supabase.from(c.tabella).insert(payload).select().single();
  if (error) throw mapGruError(error);
  return data;
};

export const gruAggiorna = async (entita, id, payload) => {
  const c = config(entita);
  const { data, error } = await supabase.from(c.tabella).update(payload).eq('id', id).select().maybeSingle();
  if (error) throw mapGruError(error);
  if (!data) throw new GruError({ code: 'NESSUN_RECORD', message: MESSAGGI_CODICE.NESSUN_RECORD });
  return data;
};

export const gruImpostaAttivo = (entita, id, attivo) => gruAggiorna(entita, id, { attivo });

/** Cancellazione tramite funzione SQL: se l'elemento è collegato ad altri dati -> RECORD_IN_USO. */
export const gruElimina = async (entita, id) => {
  const c = config(entita);
  const { data, error } = await supabase.rpc('gru_elimina_record', { p_tabella: c.tabella, p_id: id });
  if (error) throw mapGruError(error);
  return data;
};

// ─── EAN ─────────────────────────────────────────────────────────────────────

/** Controllo di comodo lato client (13 cifre + cifra di controllo). L'autorità resta il database. */
export const eanValido = (ean) => {
  if (!/^[0-9]{13}$/.test(String(ean ?? ''))) return false;
  const cifre = String(ean).split('').map(Number);
  const somma = cifre.slice(0, 12).reduce((acc, c, i) => acc + c * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (somma % 10)) % 10 === cifre[12];
};

/** EAN-13 interno (prefisso 2) mai ripetuto, generato dal database. */
export const gruGeneraEan = async () => {
  const { data, error } = await supabase.rpc('gru_genera_ean_interno');
  if (error) throw mapGruError(error);
  return data;
};

// ─── Articoli e giacenza ─────────────────────────────────────────────────────

/** Nuovo UUID per l'idempotenza: un solo valore per apertura di modal (doppio click = stessa richiesta). */
export const nuovaChiaveIdempotenza = () => crypto.randomUUID();

/** Crea un articolo con giacenza 0 (la giacenza iniziale si registra con gruRegistraCarico). */
export const gruCreaArticolo = async ({
  ean, nome, repartoId, unitaId, qtaMin = 0, attributi = {},
  codiceSap = null, codiceFornitore = null, descrizione = null, ubicazioneId = null, note = null,
}) => {
  const { data, error } = await supabase.rpc('gru_crea_articolo', {
    p_ean: ean,
    p_nome: nome,
    p_reparto_id: repartoId,
    p_unita_id: unitaId,
    p_qta_min: qtaMin,
    p_attributi: attributi,
    p_codice_sap: codiceSap,
    p_codice_fornitore: codiceFornitore,
    p_descrizione: descrizione,
    p_ubicazione_id: ubicazioneId,
    p_note: note,
  });
  if (error) throw mapGruError(error);
  return data; // { id, ean }
};

/** Anagrafica articolo (mai la giacenza: quella cambia solo con i movimenti). */
export const gruAggiornaArticolo = (id, payload) => {
  const { qta, ...senzaQta } = payload;
  return gruAggiorna('articoli', id, senzaQta);
};

export const gruRegistraCarico = async ({ chiave, articoloId, qta, motivo = null, ordineId = null }) => {
  const { data, error } = await supabase.rpc('gru_registra_carico', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_motivo: motivo,
    p_intervento_ordine_id: ordineId,
  });
  if (error) throw mapGruError(error);
  return data;
};

export const gruRegistraRettifica = async ({ chiave, articoloId, delta, motivo }) => {
  const { data, error } = await supabase.rpc('gru_registra_rettifica', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta_delta: delta,
    p_motivo: motivo,
  });
  if (error) throw mapGruError(error);
  return data;
};

/**
 * Correzione diretta di giacenza: NON compare nello Storico e non richiede nota
 * (movimento di tipo 'correzione': conta nella somma dei movimenti, quindi l'audit resta coerente).
 */
export const gruRegistraCorrezione = async ({ chiave, articoloId, delta }) => {
  const { data, error } = await supabase.rpc('gru_registra_correzione', {
    p_idempotency_key: chiave,
    p_articolo_id: articoloId,
    p_qta_delta: delta,
  });
  if (error) throw mapGruError(error);
  return data;
};

// ─── Interventi ──────────────────────────────────────────────────────────────

/**
 * Registra un intervento (Sostituzione = scarico, Rientrato = carico, Ordinato = nessuna variazione)
 * in un'unica transazione. Ritorna { intervento_id, movimento_id, qta, qta_prima, stato, duplicato }.
 */
export const gruRegistraIntervento = async ({
  chiave, tipoId, gruId, articoloId, qta, componenteId = null, causaId = null,
  dataIntervento = null, note = null, consegnaPrevista = null, ordineId = null,
}) => {
  const { data, error } = await supabase.rpc('gru_registra_intervento', {
    p_idempotency_key: chiave,
    p_tipo_id: tipoId,
    p_gru_id: gruId,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_componente_id: componenteId,
    p_causa_id: causaId,
    p_data_intervento: dataIntervento,
    p_note: note,
    p_data_consegna_prevista: consegnaPrevista,
    p_ordine_id: ordineId, // solo per i Rientrati: ordine aperto da chiudere (in tutto o in parte) con questo rientro
  });
  if (error) throw mapGruError(error);
  return data;
};

/** Cambia la data di consegna prevista di un ordine aperto oppure lo annulla. */
export const gruAggiornaOrdine = async ({ interventoId, consegnaPrevista = null, annulla = false }) => {
  const { data, error } = await supabase.rpc('gru_aggiorna_ordine', {
    p_intervento_id: interventoId,
    p_data_consegna_prevista: consegnaPrevista,
    p_annulla: annulla,
  });
  if (error) throw mapGruError(error);
  return data;
};

/**
 * Storno di un movimento (unico modo di correggere lo Storico, che è append-only):
 * scrive un movimento opposto collegato. Sostituzione/Rientrato annullano anche l'intervento;
 * un Carico di un ordine riporta l'ordine a "In ordine". Un movimento si storna una sola volta.
 */
export const gruStornaMovimento = async ({ chiave, movimentoId, motivo }) => {
  const { data, error } = await supabase.rpc('gru_storna_movimento', {
    p_idempotency_key: chiave,
    p_movimento_id: movimentoId,
    p_motivo: motivo,
  });
  if (error) throw mapGruError(error);
  return data;
};

/**
 * Modifica di un intervento in una sola transazione.
 *  - modalita 'semplice': nessun nuovo movimento (gru, componente, causa, nota, data; per un ordine aperto anche articolo/quantità);
 *  - modalita 'storno': storno del movimento originale + nuovo intervento con nuovo movimento (l'originale diventa Annullato);
 *  - modalita 'nessuna_modifica': nulla è cambiato.
 */
export const gruModificaIntervento = async ({
  chiave, interventoId, tipoId, gruId, articoloId, qta, componenteId = null, causaId = null,
  dataIntervento = null, note = null, consegnaPrevista = null,
}) => {
  const { data, error } = await supabase.rpc('gru_modifica_intervento', {
    p_idempotency_key: chiave,
    p_intervento_id: interventoId,
    p_tipo_id: tipoId,
    p_gru_id: gruId,
    p_articolo_id: articoloId,
    p_qta: qta,
    p_componente_id: componenteId,
    p_causa_id: causaId,
    p_data_intervento: dataIntervento,
    p_note: note,
    p_data_consegna_prevista: consegnaPrevista,
  });
  if (error) throw mapGruError(error);
  return data;
};

/** "Elimina" un intervento: storno del movimento (giacenza ripristinata) e intervento Annullato. Nulla viene cancellato. */
export const gruAnnullaIntervento = async ({ chiave, interventoId }) => {
  const { data, error } = await supabase.rpc('gru_annulla_intervento', {
    p_idempotency_key: chiave,
    p_intervento_id: interventoId,
  });
  if (error) throw mapGruError(error);
  return data;
};

// ─── Statistiche ─────────────────────────────────────────────────────────────

/**
 * Dati aggregati per le statistiche: una riga per mese × gru × reparto × causa × tipo × articolo
 * (esclusi gli interventi annullati). Letti a blocchi di 1000 (.range) con ordinamento totale,
 * così nessuna riga viene saltata o ripetuta tra una pagina e l'altra.
 * Ritorna { righe, troncato }.
 */
export const gruStatistiche = async (massimoPagine = 30) => {
  const perPagina = 1000;
  let righe = [];
  for (let pagina = 0; pagina < massimoPagine; pagina += 1) {
    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await supabase
      .from('gru_v_stat_mensile')
      .select('mese, anno, mese_num, gru_id, gru_codice, reparto_id, reparto_label, causa_id, causa_label, tipo_id, tipo_key, articolo_id, ean, nome, n_interventi, qta_totale')
      .order('mese', { ascending: true })
      .order('gru_id', { ascending: true })
      .order('reparto_id', { ascending: true })
      .order('causa_id', { ascending: true, nullsFirst: true })
      .order('tipo_id', { ascending: true })
      .order('articolo_id', { ascending: true })
      .range(pagina * perPagina, pagina * perPagina + perPagina - 1);
    if (error) throw mapGruError(error);
    righe = righe.concat(data ?? []);
    if (!data || data.length < perPagina) return { righe, troncato: false };
  }
  return { righe, troncato: true };
};

/**
 * Chiude un ordine aperto SENZA nuovi movimenti di magazzino (la merce è già stata caricata in altro modo).
 * Registra una ricezione "manuale" pari al residuo: ordinato = ricevuto.
 */
export const gruSegnaOrdineRicevuto = async ({ interventoId }) => {
  const { data, error } = await supabase.rpc('gru_segna_ordine_ricevuto', {
    p_intervento_id: interventoId,
  });
  if (error) throw mapGruError(error);
  return data;
};
